import { TFunction } from "i18next";
import {
  MaterialStockItem,
  PackagingItem,
  ReportItem,
  ReportType,
} from "shared/api";

export const reportTypes: Record<ReportType, string> = {
  [ReportType.MATERIAL_ITEM]: "Material Item",
  [ReportType.REEL_LABEL]: "Reel Label",
  [ReportType.PACKAGING]: "Packaging",
  [ReportType.PALLET_LABEL]: "Pallet Label",
  [ReportType.REEL_LAB_ARCHIVE]: "Reel Lab Archive",
  [ReportType.LAB_REEL_ARCHIVE]: "Reel Lab Archive",
  [ReportType.ORDER]: "Order",
  [ReportType.MULTIPLE_ORDER]: "Multiple Order",
  [ReportType.MULTIPLE_PACKAGING]: "Multiple Packaging",
  [ReportType.LOADING_LIST]: "Loading List",
  [ReportType.MULTIPLE_ORDER_PACKAGING]: "Multiple Order Packaging",
  [ReportType.LOADING_LIST_MULTIPLE]: "Loading List Multiple",
  [ReportType.MULTIPLE_MATERIAL]: "Multiple Material",
  [ReportType.MULTIPLE_QUALITY]: "Multiple Quality",
  [ReportType.LAB_VIEW_FULL_ORDER]: "Lab View Full Order",
  [ReportType.MULTIPLE_MATERIAL_ITEM]: "Multiple Material Item",
  [ReportType.MANAGEMENT]: "Managment",
  [ReportType.MANAGEMENT_DAILY]: "Management Daily",
  [ReportType.MANAGEMENT_WEEKLY]: "Management Weekly",
  [ReportType.DRIGGING_RIGG]: "Drigging Rigg",
  [ReportType.STOCK]: "Stock",
  [ReportType.MULTIPLE_COMPANY_PACKAGING]: "Company Loading List - Multiple",
  [ReportType.QUALITY_ADHESION]: "Quality - Adhesion",
  [ReportType.LOCAL]: "Local",
  [ReportType.REEL_LAB_RESULT_MULTIPLE]: "Reel Lab Result Multiple",
};

export const renderReportType = (report: ReportItem) => {
  const status = reportTypes[report?.report_type as ReportType];
  return status || "Unknown Type";
};

// Reports whose preview (previsualization) must always be rendered as XLSX,
// regardless of the ReportFormat selected by the user. The selected format is
// only applied when generating the final (downloaded) report, not the preview.
// Matched against `template_html` (stable code) so renamed reports keep working
// and legacy "Z -OLD-" duplicates (empty template_html) are excluded.
export const XLSX_PREVIEW_ONLY_REPORT_CODES = [
  "REEL_LAB_RESULT_MULTIPLE",
  "SPECIAL_XLS",
  "LAB_VIEW_FULL_ORDER",
];

// Custom carbone types whose backend implementation only ever produces XLS.
// These reports offer no format choice at all — the ReportFormat selector is
// hidden. Distinct from XLSX_PREVIEW_ONLY_REPORT_CODES above, where only the
// preview is forced to XLSX and the user still picks the download format.
export const XLS_ONLY_CARBONE_TYPES = ["SPECIAL_XLS", "LAB_VIEW_FULL_ORDER"];

export const isXlsOnlyReport = (report?: ReportItem): boolean => {
  if (!report?.carbone_type) return false;

  return XLS_ONLY_CARBONE_TYPES.includes(report.carbone_type);
};

export const isXlsxPreviewOnlyReport = (report?: ReportItem): boolean => {
  const isXlsxPreviewOnlyCode =
    !!report?.template_html &&
    XLSX_PREVIEW_ONLY_REPORT_CODES.includes(report.template_html);

  return isXlsOnlyReport(report) || isXlsxPreviewOnlyCode;
};

export const renderIsDefaultString = (report: ReportItem) => {
  return report?.is_default ? "Default" : "Not Default";
};

// const normalizeNameToCode = (name: string) =>
//   name
//     .toUpperCase()
//     .replace(/\s*-\s*/g, "_")
//     .replace(/\s+/g, "_");

export const renderReportTypeTitle = (reportType: string, t: TFunction) =>
  t(`reportTypes.${reportType}`, {
    defaultValue: reportType
      .replaceAll("_", " ")
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase()),
  });

export const renderReportTitle = (report: ReportItem) => {
  return report.name;
};

export const getPackagingDataFromMateriaStockItem = (
  materialStockItem: MaterialStockItem
) => {
  const materialPackagingData = materialStockItem?.packaging as PackagingItem;
  const packagingMaterialId =
    materialPackagingData?.material as unknown as number;

  return {
    id: materialPackagingData?.id,
    name: materialPackagingData?.name,
    material: packagingMaterialId,
    order: materialPackagingData?.order,
  };
};

export const getProductCardRequestParams = (productCardListId: number[]) => {
  if (productCardListId.length === 1) {
    return {
      product_card_id: productCardListId[0],
      product_card_list_id: [] as number[],
    };
  }

  return {
    product_card_id: null,
    product_card_list_id: productCardListId,
  };
};
